import { useQuery } from "@tanstack/react-query";
import { ReceiptIcon, WrenchIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useFormat } from "@/lib/format";
import { useModule } from "@/lib/modules";
import { useTRPC } from "@/lib/trpc";

/**
 * A service history entry's line (`ServiceHistoryEntryExtra`): who did the work and what its
 * invoices came to. Every entry reads the vehicle's one cached query.
 */
export function HistoryExtra({
  vehicleId,
  workOrderId,
}: {
  vehicleId: string;
  workOrderId: string;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useFormat();
  const active = useModule("vehicleManagement");
  const { data } = useQuery({
    ...trpc.vehicleManagement.vehicle.queryOptions({ vehicleId }),
    enabled: active,
  });
  const entry = data?.workOrders[workOrderId];
  if (!active || !entry || (!entry.performedBy && !entry.invoicesMinor)) return null;
  const currency = data.currency;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
      {entry.performedBy ? (
        <span className="flex items-center gap-1">
          <WrenchIcon className="size-3.5 shrink-0" />
          {t("vehicleManagement.atProvider", { name: entry.performedBy.name })}
        </span>
      ) : null}
      {entry.invoicesMinor ? (
        <span className="flex items-center gap-1 tabular">
          <ReceiptIcon className="size-3.5 shrink-0" />
          {t("vehicleManagement.invoicesAmount", {
            amount: format.money(entry.invoicesMinor, currency),
          })}
        </span>
      ) : null}
    </div>
  );
}
